import { workspaceToolGroups } from "../shared/workspace-tool-catalog";
import { LibraryPanel } from "./LibraryPanel";
import { Library } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  ChevronUp,
  FilePlus2,
  FileText,
  Plus,
  Search,
  Table2,
  X,
  Copy,
  Eraser,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { WorkspaceModel } from "./useWorkspace";
import { SheetCreationFlyout } from "./SheetCreationFlyout";
import { quickAddSheet } from "./quick-add";
import { TracePlacementPreview } from "./TracePlacementTool";
import { useAgentThreads } from "./agent-threads";
import { askAgentShortcut } from "./ask-agent";
import { BoardSearch } from "./BoardSearch";
import { TableCreation } from "./TableCreation";
import "./tool-discovery.css";

export type CreationMenu =
  "add" | "sheet" | "more" | "search" | "table" | "library" | null;
type Entry = {
  id: string;
  label: string;
  icon: LucideIcon;
  hint: string;
  run: () => void;
  /** Optional shortcut for a double-click on the toolbar button. */
  runDouble?: () => void;
  active?: boolean;
  disabled?: boolean;
};

export function ToolPalette({
  model,
  menu,
  setMenu,
  onCreateDocument,
  saveRequest = 0,
  initialLibraryItem,
  onLibrarySaveHandled,
  onLibraryItemHandled,
}: {
  model: WorkspaceModel;
  menu: CreationMenu;
  setMenu: (value: CreationMenu) => void;
  onCreateDocument: () => void;
  saveRequest?: number;
  initialLibraryItem?: string;
  onLibrarySaveHandled?: () => void;
  onLibraryItemHandled?: () => void;
}) {
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const [focusedGroup, setFocusedGroup] = useState("Workspace");
  const [libraryMounted, setLibraryMounted] = useState(menu === "library");
  useEffect(() => {
    if (menu === "library") setLibraryMounted(true);
  }, [menu]);
  const { canEdit, canComment, tool, choose, editor } = model;
  const agents = useAgentThreads();
  const close = (restoreFocus = false) => {
    setMenu(null);
    if (restoreFocus) trigger.current?.focus();
  };
  useEffect(() => {
    if (!menu || menu === "library") return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setMenu(null);
    };
    document.addEventListener("pointerdown", outside);
    if (menu === "more") {
      const buttons = root.current?.querySelectorAll<HTMLButtonElement>(
        `[data-tool-group="${focusedGroup}"] .dock-tool-option:not(:disabled)`,
      );
      Array.from(buttons || [])
        .find((button) => button.getClientRects().length > 0)
        ?.focus();
    }
    return () => document.removeEventListener("pointerdown", outside);
  }, [menu, focusedGroup, setMenu]);
  const open = (next: CreationMenu, button?: HTMLButtonElement) => {
    if (button) trigger.current = button;
    model.setPanel(null);
    setMenu(next);
  };
  const run = (action: () => void) => {
    close();
    action();
  };
  const askAgent =
    agents.enabled && canEdit
      ? ` ${askAgentShortcut()} asks the agent about the selection.`
      : "";
  const hints: Record<string, string> = {
    move: "Drag the selected objects together, preserving their spacing.",
    polyline:
      "Click successive points; double-click or Enter finishes, Backspace removes the last point.",
    circle:
      "Drag from the centre to set a radius; drag the handle to adjust it later.",
    curve:
      "Click two endpoints and a control point; drag the three handles to edit the curve.",
    wall: "Click the wall path, then Enter or double-click to finish; set its thickness in the tool settings.",
    door: "Set the width and swing, then click a wall to place an attached door.",
    window: "Set the width, then click a wall to place an aligned window.",
    leader:
      "Click a detail and the label position, then edit the annotation text.",
    select:
      "Click an object or drag a box to select; use its handles to resize or rotate.",
    hand: "Drag to pan around the board; Space-drag also works in other tools.",
    text: "Click for a text box or drag to choose its width, then type.",
    dimension:
      "Pick two endpoints to measure distance on a calibrated drawing.",
    angle: "Pick a side, the corner and the other side to measure the angle.",
    area: "Click boundary points and close the outline to measure a calibrated area.",
    pin: `Click to pin feedback; open a pin to reply or resolve the discussion.${askAgent}`,
    note: "Click to place a note and type; drag the note to move it.",
    cloud:
      "Drag a revision cloud around a changed region; select it to add a label.",
    arrow: "Drag toward a detail, then adjust its arrowheads or add a label.",
    line: "Click two endpoints; hold Alt/Option to bypass alignment assistance.",
    draw: "Press and drag to sketch a freehand stroke; release to finish.",
    geo: "Drag between opposite corners, then adjust the rectangle with its handles.",
    polygon: "Click vertices; click the first corner or press Enter to close.",
    highlight: "Drag a translucent stroke over a detail to highlight it.",
    eraser: "Drag over whole objects to erase them; Undo restores them.",
    "comment-area":
      "Sweep a region to attach a discussion without adding export artwork.",
  };
  const labels: Record<string, string> = {
    draw: "Freeform",
    angle: "Angular dimension",
    area: "Area",
    pin: "Comment",
    polygon: "Polygon",
    "comment-area": "Area comment",
    eraser: "Eraser",
  };
  const drawing = (id: string): Entry => {
    const entry = model.toolList.find((t) => t.id === id)!;
    return {
      id,
      label: labels[id] || entry.label,
      icon: id === "eraser" ? Eraser : entry.icon,
      hint: hints[id],
      active: tool === id,
      run: () => run(() => choose(id)),
    };
  };
  const page: Entry = {
    id: "sheet",
    label: "Page",
    icon: FilePlus2,
    hint: "Click to choose a size, orientation and name. Double-click to add an A3 sheet.",
    active: menu === "sheet",
    run: () => open("sheet"),
    runDouble: () =>
      run(() => {
        if (!canEdit) return;
        model.setPanel(null);
        const sheet = quickAddSheet(editor);
        model.setPaper("A3");
        model.setLandscape(true);
        model.focusSheet(sheet);
      }),
  };
  const search: Entry = {
    id: "search",
    label: "Search",
    icon: Search,
    hint: "Find sheets, text and named objects on this board.",
    active: menu === "search",
    run: () => open("search"),
  };
  const documentEntry: Entry = {
    id: "document",
    label: "Document",
    icon: FileText,
    hint: "Write a brief or meeting notes directly on the canvas.",
    run: () => run(onCreateDocument),
  };
  const upload: Entry = {
    id: "import",
    label: "Import",
    icon: Plus,
    hint: "Add images, PDFs, DXF drawings, documents, spreadsheets or 3D references.",
    run: () => run(model.openFilePicker),
  };
  const table: Entry = {
    id: "table",
    label: "Table",
    icon: Table2,
    hint: "Choose rows and columns, then edit the cells on the board.",
    run: () => open("table"),
  };
  const library: Entry = {
    id: "library",
    label: "My Library",
    icon: Library,
    hint: "Save selected objects to your private account library, then preview or drag an item onto a sheet.",
    active: menu === "library",
    run: () => open("library"),
  };
  const trace: Entry = {
    id: "trace",
    label: "Tracing paper",
    icon: Copy,
    hint: "Move the paper preview over a reference and click to place it; Escape cancels.",
    active: tool === "trace-paper",
    run: () =>
      run(() => {
        model.annotation.end();
        editor.setCurrentTool("trace-paper", { over: model.active });
        editor.focus();
      }),
  };
  const workspaceEntries:Record<(typeof workspaceToolGroups.Workspace)[number],Entry>={select:drawing("select"),sheet:page,document:documentEntry,import:upload,library,table,search,move:drawing("move")};
  const groups = [
    {
      label: "Workspace",
      entries:workspaceToolGroups.Workspace.filter(id=>canEdit||["select","library","search","move"].includes(id)).map(id=>workspaceEntries[id]),
      quick: ["select", "sheet", "import"],
    },
    {
      label: "Draw",
      entries: canEdit ? workspaceToolGroups.Draw.map(drawing) : [],
      quick: ["line", "draw", "geo"],
    },
    {
      label: "Annotate",
      entries: workspaceToolGroups.Annotate.filter((id) =>
        ["pin", "comment-area"].includes(id) ? canComment : canEdit,
      ).map(drawing),
      quick: ["text", "arrow", "pin"],
    },
    {
      label: "Measure & trace",
      entries: canEdit
        ? workspaceToolGroups.Measure.map((id) =>
            id === "trace" ? trace : drawing(id),
          )
        : [],
      quick: ["dimension", "area", "trace"],
    },
  ]
    .filter((group) => group.entries.length)
    .map((group) => ({
      ...group,
      quick: group.quick.filter((id) =>
        group.entries.some((entry) => entry.id === id),
      ),
    }));
  const expandedGroup =
    groups.find((group) => group.label === focusedGroup) || groups[0];
  const button = (entry: Entry, compact: boolean, mobileOnly = false) => (
    <button
      key={entry.id}
      type="button"
      aria-label={entry.label}
      aria-pressed={entry.active}
      disabled={entry.disabled}
      title={compact ? `${entry.label} — ${entry.hint}` : entry.hint}
      className={`${compact ? "dock-tool" : "dock-tool-option"}${entry.active ? " active" : ""}${mobileOnly ? " dock-mobile-tool-option" : ""}`}
      onClick={(event) => {
        if (compact) trigger.current = event.currentTarget;
        entry.run();
      }}
      onDoubleClick={entry.runDouble}
    >
      <entry.icon size={compact ? 19 : 18} aria-hidden="true" />
      <span className={compact ? "dock-tooltip" : undefined}>
        {entry.label}
      </span>
    </button>
  );
  return (
    <>
      <TracePlacementPreview editor={editor} />
      <div
        style={
          {
            "--tool-group-count": groups.length,
            "--tool-group-index": groups.indexOf(expandedGroup),
          } as import("react").CSSProperties
        }
        ref={root}
        className="tool-dock"
        onKeyDown={(event) => {
          if (event.key === "Escape" && menu) {
            event.preventDefault();
            event.stopPropagation();
            close(true);
          }
        }}
      >
        {(menu === "more" || menu === "add") && (
          <div
            id="tool-catalogue"
            className="tool-catalogue"
            aria-label={`${expandedGroup.label} tools`}
          >
            <section
              className="dock-card"
              data-tool-group={expandedGroup.label}
              aria-label={expandedGroup.label}
            >
              <h3>{expandedGroup.label}</h3>
              {expandedGroup.entries
                .filter(
                  (entry) =>
                    !expandedGroup.quick.slice(0, 2).includes(entry.id),
                )
                .map((entry) =>
                  button(entry, false, expandedGroup.quick.includes(entry.id)),
                )}
            </section>
          </div>
        )}
        {(libraryMounted || menu === "library") && (
          <LibraryPanel
            open={menu === "library"}
            model={model}
            close={() => close(true)}
            saveRequest={saveRequest}
            initialItem={initialLibraryItem}
            onSaveHandled={onLibrarySaveHandled}
            onItemHandled={onLibraryItemHandled}
          />
        )}
        {(menu === "sheet" || menu === "table" || menu === "search") && (
          <div
            className="dock-popover floating-surface"
            role="dialog"
            aria-label={
              menu === "sheet"
                ? "New sheet"
                : menu === "table"
                  ? "New table"
                  : "Search board"
            }
          >
            <div className="workspace-panel-heading">
              <strong>
                {menu === "sheet"
                  ? "New sheet"
                  : menu === "table"
                    ? "New table"
                    : "Search board"}
              </strong>
              <button aria-label="Close tools" onClick={() => close(true)}>
                <X size={17} />
              </button>
            </div>
            {menu === "sheet" ? (
              <SheetCreationFlyout model={model} close={() => close()} />
            ) : menu === "table" ? (
              <TableCreation model={model} close={() => close()} />
            ) : (
              <BoardSearch model={model} close={() => close()} />
            )}
          </div>
        )}
        <nav
          className="tool-dock-groups floating-surface"
          aria-label="Drawing tools"
        >
          {groups.map((group) => (
            <div
              className="dock-group"
              key={group.label}
              role="group"
              aria-label={`${group.label} shortcuts`}
            >
              {group.quick
                .map((id) => group.entries.find((entry) => entry.id === id))
                .filter((entry): entry is Entry => !!entry)
                .map((entry) => button(entry, true))}
              <button
                className={`dock-expand${group.entries.some((entry) => entry.active && !group.quick.includes(entry.id)) ? " has-active-tool" : ""}`}
                aria-label={`${group.label} tools`}
                title={`${group.label} tools`}
                aria-expanded={
                  (menu === "more" || menu === "add") &&
                  expandedGroup.label === group.label
                }
                aria-controls="tool-catalogue"
                onClick={(event) => {
                  trigger.current = event.currentTarget;
                  setFocusedGroup(group.label);
                  open(
                    menu === "more" && focusedGroup === group.label
                      ? null
                      : "more",
                  );
                }}
              >
                <ChevronUp size={13} />
              </button>
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}
